import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, MoreVert, Folder } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { projectsApi } from '../../services/benchmarkApi';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: 8,
});

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8',
    color: '#6B0F2A',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(139, 159, 232, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(139, 159, 232, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(139, 159, 232, 0.24)',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius * 2,
    borderBottomRightRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
}));

const emptyForm = {
  projectNumber: '',
  projectName: '',
  clientName: '',
  projectType: '',
  projectSubType: '',
  projectStatus: '',
  country: '',
  stateRegion: '',
  city: '',
  marketRegion: '',
  currencyCode: '',
  grossFloorArea: '',
  floorsAboveGround: '',
  basementLevels: '',
  hotelRooms: '',
  structuralSystem: '',
  facadeType: '',
  foundationType: '',
  plannedDurationMonths: '',
  actualDurationMonths: '',
  plannedStartDate: '',
  plannedFinishDate: '',
  actualStartDate: '',
  actualFinishDate: '',
  siteComplexityScore: '',
  specificationLevel: '',
};

const mapProjectRow = (p) => ({
  id: p.id || p.projectId,
  projectNumber: p.projectNumber || p.projectCode || '',
  projectName: p.projectName || '',
  clientName: p.clientName || p.owner || '',
  projectType: p.projectType || '',
  projectSubType: p.projectSubType || '',
  projectStatus: p.projectStatus || '',
  country: p.country || '',
  stateRegion: p.stateRegion || '',
  city: p.city || '',
  marketRegion: p.marketRegion || p.region || '',
  currencyCode: p.currencyCode || '',
  grossFloorArea: p.grossFloorArea != null ? String(p.grossFloorArea) : (p.area != null ? String(p.area) : ''),
  floorsAboveGround: p.floorsAboveGround != null ? String(p.floorsAboveGround) : '',
  basementLevels: p.basementLevels != null ? String(p.basementLevels) : '',
  hotelRooms: p.hotelRooms != null ? String(p.hotelRooms) : '',
  structuralSystem: p.structuralSystem || '',
  facadeType: p.facadeType || '',
  foundationType: p.foundationType || '',
  plannedDurationMonths:
    p.plannedDurationMonths != null
      ? String(p.plannedDurationMonths)
      : p.plannedDurationDays != null
        ? String(Math.round(Number(p.plannedDurationDays) / 30))
        : '',
  actualDurationMonths:
    p.actualDurationMonths != null
      ? String(p.actualDurationMonths)
      : p.actualDurationDays != null
        ? String(Math.round(Number(p.actualDurationDays) / 30))
        : '',
  plannedStartDate: p.plannedStartDate || p.startDate || '',
  plannedFinishDate: p.plannedFinishDate || p.endDate || '',
  actualStartDate: p.actualStartDate || '',
  actualFinishDate: p.actualFinishDate || '',
  siteComplexityScore: p.siteComplexityScore != null ? String(p.siteComplexityScore) : '',
  specificationLevel: p.specificationLevel || p.classLevel || '',
});

const BenchmarkProjects = () => {
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await projectsApi.list();
      setRows((res.data || []).map(mapProjectRow));
    } catch (e) {
      setError(e.message || 'Failed to load projects');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  const handleAdd = () => {
    setEditMode(false);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const handleEdit = () => {
    if (!selectedRow) {
      return;
    }
    setEditMode(true);
    setFormData({ ...selectedRow });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setFormData(emptyForm);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setError('');
    const payload = {
      projectNumber: formData.projectNumber,
      projectCode: formData.projectNumber,
      projectName: formData.projectName,
      clientName: formData.clientName,
      owner: formData.clientName,
      projectType: formData.projectType,
      projectSubType: formData.projectSubType,
      projectStatus: formData.projectStatus,
      country: formData.country,
      stateRegion: formData.stateRegion,
      city: formData.city,
      marketRegion: formData.marketRegion,
      region: formData.marketRegion,
      currencyCode: formData.currencyCode,
      grossFloorArea: formData.grossFloorArea,
      area: formData.grossFloorArea,
      floorsAboveGround: formData.floorsAboveGround
        ? Number(formData.floorsAboveGround)
        : null,
      basementLevels: formData.basementLevels ? Number(formData.basementLevels) : null,
      hotelRooms: formData.hotelRooms ? Number(formData.hotelRooms) : null,
      structuralSystem: formData.structuralSystem || null,
      facadeType: formData.facadeType || null,
      foundationType: formData.foundationType || null,
      plannedDurationMonths: formData.plannedDurationMonths
        ? Number(formData.plannedDurationMonths)
        : null,
      actualDurationMonths: formData.actualDurationMonths
        ? Number(formData.actualDurationMonths)
        : null,
      plannedStartDate: formData.plannedStartDate || null,
      startDate: formData.plannedStartDate || null,
      plannedFinishDate: formData.plannedFinishDate || null,
      endDate: formData.plannedFinishDate || null,
      actualStartDate: formData.actualStartDate || null,
      actualFinishDate: formData.actualFinishDate || null,
      siteComplexityScore: formData.siteComplexityScore
        ? Number(formData.siteComplexityScore)
        : null,
      specificationLevel: formData.specificationLevel || null,
      classLevel: formData.specificationLevel || null,
    };
    try {
      if (editMode && selectedRow) {
        await projectsApi.update(selectedRow.id, payload);
      } else {
        await projectsApi.create(payload);
      }
      handleCloseDialog();
      setSelectedRow(null);
      await loadProjects();
    } catch (e) {
      setError(e.message || 'Failed to save project');
    }
  };

  const handleMenuClick = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedRowForMenu(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleMenuItemClick = (action) => {
    if (!selectedRowForMenu) return;

    switch (action) {
      case 'Contracts':
        navigate(`/benchmark/contracts/${selectedRowForMenu.id}`, {
          state: { project: selectedRowForMenu }
        });
        break;
      case 'Cost category':
        navigate(`/benchmark/cost-category/${selectedRowForMenu.id}`, {
          state: { project: selectedRowForMenu }
        });
        break;
      default:
        break;
    }
    
    handleMenuClose();
  };

  const columns = [
    {
      field: 'projectNumber',
      headerName: 'Project Number',
      width: 150,
    },
    {
      field: 'projectName',
      headerName: 'Project Name',
      width: 220,
      minWidth: 200,
    },
    {
      field: 'clientName',
      headerName: 'Client',
      width: 180,
      minWidth: 150,
    },
    {
      field: 'projectType',
      headerName: 'Type',
      width: 130,
    },
    {
      field: 'projectStatus',
      headerName: 'Status',
      width: 120,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 130,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 120,
    },
    {
      field: 'marketRegion',
      headerName: 'Region',
      width: 140,
    },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      width: 100,
    },
    {
      field: 'grossFloorArea',
      headerName: 'GFA',
      width: 110,
    },
    {
      field: 'floorsAboveGround',
      headerName: 'Floors',
      width: 90,
    },
    {
      field: 'basementLevels',
      headerName: 'Basements',
      width: 100,
    },
    {
      field: 'hotelRooms',
      headerName: 'Hotel Rooms',
      width: 110,
    },
    {
      field: 'structuralSystem',
      headerName: 'Structure',
      width: 150,
    },
    {
      field: 'facadeType',
      headerName: 'Facade',
      width: 140,
    },
    {
      field: 'specificationLevel',
      headerName: 'Quality',
      width: 110,
    },
    {
      field: 'plannedDurationMonths',
      headerName: 'Plan Mo',
      width: 90,
    },
    {
      field: 'actualDurationMonths',
      headerName: 'Act Mo',
      width: 90,
    },
    {
      field: 'plannedStartDate',
      headerName: 'Start',
      width: 120,
    },
    {
      field: 'plannedFinishDate',
      headerName: 'Finish',
      width: 120,
    },
    {
      field: 'siteComplexityScore',
      headerName: 'Complexity Score',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <HeaderContainer>
            <TitleBar>
              <Title variant="h6">
                <Folder fontSize="small" />
                Projects
              </Title>
              <ButtonGroup>
                <Tooltip title="Add New Project">
                  <IconButton 
                    color="primary" 
                    onClick={handleAdd} 
                    size="small"
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Selected Project">
                  <IconButton 
                    color="primary" 
                    onClick={handleEdit} 
                    disabled={!selectedRow} 
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
            </TitleBar>
            {error && (
              <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </HeaderContainer>
          
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {loading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', zIndex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <StyledDataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              density="compact"
              autoHeight={false}
              headerHeight={48}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuItemClick('Contracts')}>
          <ListItemText>Contracts</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Cost category')}>
          <ListItemText>Cost category</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
          {editMode ? 'Edit Project' : 'Add New Project'}
        </DialogTitle>
        <DialogContent dividers sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Number"
                name="projectNumber"
                value={formData.projectNumber}
                onChange={handleInputChange}
                required
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                required
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Name"
                name="clientName"
                value={formData.clientName}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Project Type</InputLabel>
                <Select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  label="Project Type"
                >
                  <MenuItem value="Hotel">Hotel</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Residential">Residential</MenuItem>
                  <MenuItem value="Industrial">Industrial</MenuItem>
                  <MenuItem value="Mixed Use">Mixed Use</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                  <MenuItem value="Educational">Educational</MenuItem>
                  <MenuItem value="Technology">Technology</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Sub Type"
                name="projectSubType"
                value={formData.projectSubType}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Status"
                name="projectStatus"
                value={formData.projectStatus}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State / Region"
                name="stateRegion"
                value={formData.stateRegion}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Market Region"
                name="marketRegion"
                value={formData.marketRegion}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Currency Code"
                name="currencyCode"
                value={formData.currencyCode}
                onChange={handleInputChange}
                size="small"
                margin="dense"
                placeholder="AED / SAR / QAR"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Gross Floor Area (m²)"
                name="grossFloorArea"
                value={formData.grossFloorArea}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Floors Above Ground"
                name="floorsAboveGround"
                type="number"
                value={formData.floorsAboveGround}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Basements"
                name="basementLevels"
                type="number"
                value={formData.basementLevels}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Hotel Rooms"
                name="hotelRooms"
                type="number"
                value={formData.hotelRooms}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Quality / Specification Level"
                name="specificationLevel"
                value={formData.specificationLevel}
                onChange={handleInputChange}
                size="small"
                margin="dense"
                placeholder="Luxury / Premium"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Structural System"
                name="structuralSystem"
                value={formData.structuralSystem}
                onChange={handleInputChange}
                size="small"
                margin="dense"
                placeholder="Reinforced Concrete"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Facade Type"
                name="facadeType"
                value={formData.facadeType}
                onChange={handleInputChange}
                size="small"
                margin="dense"
                placeholder="Curtain Wall"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Foundation Type"
                name="foundationType"
                value={formData.foundationType}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Planned Duration (Mo)"
                name="plannedDurationMonths"
                type="number"
                value={formData.plannedDurationMonths}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Actual Duration (Mo)"
                name="actualDurationMonths"
                type="number"
                value={formData.actualDurationMonths}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Planned Start Date"
                name="plannedStartDate"
                type="date"
                value={formData.plannedStartDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Planned Finish Date"
                name="plannedFinishDate"
                type="date"
                value={formData.plannedFinishDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Actual Start Date"
                name="actualStartDate"
                type="date"
                value={formData.actualStartDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Actual Finish Date"
                name="actualFinishDate"
                type="date"
                value={formData.actualFinishDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Site Complexity Score"
                name="siteComplexityScore"
                type="number"
                value={formData.siteComplexityScore}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </RootDiv>
  );
};

export default BenchmarkProjects;
